import { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { CHART_TYPES, getOrgChart, type ChartType, type OrgChartCache, type OrgChartNode } from '../../api/orgApi.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/** The cache payload is opaque; pull a renderable root node out of common shapes. */
function extractRoot(chartData: Record<string, unknown> | null | undefined): OrgChartNode | null {
  if (!chartData || typeof chartData !== 'object') return null;
  const cd = chartData as Record<string, unknown>;
  if (Array.isArray(cd.children) || cd.empExtId || cd.id || cd.fullName || cd.label) return cd as OrgChartNode;
  if (cd.root && typeof cd.root === 'object') return cd.root as OrgChartNode;
  if (Array.isArray(cd.nodes) && cd.nodes.length > 0) return { children: cd.nodes as OrgChartNode[] };
  return null;
}

function labelOf(n: OrgChartNode): string {
  return n.fullName ?? n.label ?? n.name ?? n.employeeCode ?? n.empExtId ?? n.id ?? '(unnamed)';
}

export function OrgChartPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [cache, setCache] = useState<OrgChartCache | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartType, setChartType] = useState<ChartType>('ORGANIZATION');

  useEffect(() => {
    setLoading(true);
    setError(null);
    getOrgChart(token, chartType)
      .then(setCache)
      .catch(e => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token, chartType]);

  const root = extractRoot(cache?.chartData);

  return (
    <main aria-label="org-chart" style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h1>{t('org.orgchart.title')}</h1>
        <div>
          {CHART_TYPES.map(ct => (
            <Button
              key={ct}
              type="button"
              variant={chartType === ct ? 'primary' : 'secondary'}
              onClick={() => setChartType(ct)}
              style={{ marginLeft: 8 }}
            >
              {ct.charAt(0) + ct.slice(1).toLowerCase()}
            </Button>
          ))}
        </div>
      </div>
      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {loading && <p>{t('common.loading')}</p>}
      {!loading && !cache && !error && (
        <p>{t('org.orgchart.noCache', { type: chartType.toLowerCase() })}</p>
      )}
      {!loading && cache && !root && (
        <p>
          {t('org.orgchart.cacheExists', { type: chartType.toLowerCase() })}{cache.generatedAt ? t('org.orgchart.generatedSuffix', { date: at(cache.generatedAt) }) : ''}{t('org.orgchart.noNodes')}
        </p>
      )}
      {root && <OrgNode node={root} depth={0} />}
    </main>
  );
}

function OrgNode({ node, depth }: { node: OrgChartNode; depth: number }): ReactNode {
  const meta = [node.designationName, node.departmentName].filter(Boolean).join(' — ');
  const key = node.empExtId ?? node.id ?? labelOf(node);
  return (
    <div style={{ marginLeft: depth * 24, padding: 8, borderLeft: depth > 0 ? '2px solid var(--color-border-default)' : 'none' }}>
      <div style={{ padding: 8, background: 'var(--color-bg-muted)', borderRadius: 6, marginBottom: 4 }}>
        <strong>{labelOf(node)}</strong>
        {meta && <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--color-text-muted)' }}>{meta}</span>}
      </div>
      {node.children?.map((child, i) => <OrgNode key={`${key}-${i}`} node={child} depth={depth + 1} />)}
    </div>
  );
}
